import React from 'react';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import BuildOutlinedIcon from '@material-ui/icons/BuildOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DirectionsCarOutlinedIcon from '@material-ui/icons/DirectionsCarOutlined';
import DirectionsOutlinedIcon from '@material-ui/icons/DirectionsOutlined';
import EventOutlinedIcon from '@material-ui/icons/EventOutlined';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import HomeWorkOutlinedIcon from '@material-ui/icons/HomeWorkOutlined';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined';
import ScheduleOutlinedIcon from '@material-ui/icons/ScheduleOutlined';
import SpeedOutlinedIcon from '@material-ui/icons/SpeedOutlined';
import AvailabilityCardItem from './AvailabilityCardItem';
import {
  ClickableCardButton,
  FrotaHeaderIconButton,
  FrotaSectionsWrap,
  FrotaStatusPill,
  FrotaTripBlockGrid,
  FrotaTripCell,
  FrotaTripCellLabel,
  FrotaTripCellMeta,
  FrotaTripCellText,
  FrotaTripCellValue,
  FrotaTripHeader,
  FrotaTripMetricsWrap,
  FrotaTripRouteGrid,
  FrotaTripSection,
  FrotaTripSubtitle,
  FrotaTripTitle,
  FrotaTripTitleRow
} from '../DisponibilidadeFrota-styled';
import { emptyValue } from '../utils/constants';
import { formatPopupDateTime } from '../utils/formatters';

const hasSelectedText = () => {
  if (typeof window === 'undefined' || typeof window.getSelection !== 'function') return false;
  return String(window.getSelection() || '').trim().length > 0;
};

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return emptyValue;
  return value;
};

const getStatusTone = value => {
  const normalized = String(value || '').toLowerCase();

  if (!normalized || normalized === emptyValue) return 'neutral';
  if (normalized.includes('indispon')) return 'danger';
  if (normalized.includes('final')) return 'success';
  if (normalized.includes('dispon')) return 'success';
  if (normalized.includes('inici')) return 'blue';
  if (normalized.includes('rota')) return 'blue';
  if (normalized.includes('problema')) return 'warning';
  if (normalized.includes('manuten')) return 'warning';

  return 'neutral';
};

const getStatusPatioIcon = statusPatio => {
  const iconProps = { style: { fontSize: 11 } };

  switch (Number(statusPatio)) {
    case 1:
      return <CheckCircleOutlineIcon {...iconProps} />;
    case 2:
      return <HighlightOffOutlinedIcon {...iconProps} />;
    case 3:
      return <DirectionsOutlinedIcon {...iconProps} />;
    case 4:
      return <ErrorOutlineOutlinedIcon {...iconProps} />;
    case 5:
      return <BuildOutlinedIcon {...iconProps} />;
    default:
      return <HomeWorkOutlinedIcon {...iconProps} />;
  }
};

const buildRouteDisplay = local => ({
  location: getDisplayValue(local?.cidadeEstado),
  address: getDisplayValue(local?.endereco),
  client: getDisplayValue(local?.cliente)
});

const renderMetricCell = ({ icon, label, value, withDivider = false }) => (
  <FrotaTripCell key={label} $withDivider={withDivider}>
    {icon}
    <FrotaTripCellText>
      <FrotaTripCellLabel>{label}</FrotaTripCellLabel>
      <FrotaTripCellValue>{getDisplayValue(value)}</FrotaTripCellValue>
    </FrotaTripCellText>
  </FrotaTripCell>
);

const FrotaAvailabilityCard = ({
  item,
  onSelect,
  selected,
  expanded,
  onExpandedChange,
  onPositionClick,
  getPositionStatus,
  getPositionTooltip,
  showPositionButton = false
} = {}) => {
  const handleSelect = () => {
    if (!onSelect || hasSelectedText()) return;
    onSelect(item);
  };

  const handleKeyDown = event => {
    if (!onSelect) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    handleSelect();
  };

  const handlePositionClick = event => {
    event.stopPropagation();
    onPositionClick?.(item);
  };

  const positionStatus = showPositionButton ? (getPositionStatus?.(item) || 'unknown') : 'unknown';
  const positionTooltip = showPositionButton ? (getPositionTooltip?.(item) || 'Ver posicao') : '';

  const summaryItems = [
    {
      icon: <DirectionsCarOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />,
      label: 'Veiculo',
      value: item.modeloVeicular,
      wrap: true
    },
    {
      icon: <PersonOutlineOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />,
      label: 'Gestor',
      value: item.gestor,
      wrap: true
    },
    {
      icon: <LocalOfferOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />,
      label: 'Plotagem',
      value: item.plotagem,
      wrap: true
    }
  ];

  const detailSections = (item?.cargaViagemDetalhes || []).map(detail => ({
    title: `Carga ${getDisplayValue(detail.numeroCarga)} | Viagem ${getDisplayValue(detail.viagemId)}`,
    subtitle: getDisplayValue(detail.tipoOperacao),
    status: getDisplayValue(detail.statusViagem),
    routeOrigin: buildRouteDisplay(detail.origem),
    routeDestination: buildRouteDisplay(detail.destino),
    topMetrics: [
      {
        label: 'Numero do Pedido',
        value: detail.numeroPedido,
        icon: <AssignmentOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      },
      {
        label: 'Data Carregamento',
        value: detail.dataCarregamento,
        icon: <EventOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      },
      {
        label: 'Data Agendamento',
        value: detail.dataAgendamento,
        icon: <EventOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      }
    ],
    scheduleMetrics: [
      {
        label: 'Data Inicio',
        value: formatPopupDateTime(detail.dataInicio),
        icon: <ScheduleOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      },
      {
        label: 'Data Fim',
        value: formatPopupDateTime(detail.dataFim),
        icon: <ScheduleOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      },
      {
        label: 'Previsao Chegada',
        value: formatPopupDateTime(detail.previsaoChegada),
        icon: <ScheduleOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      }
    ],
    distanceMetrics: [
      {
        label: 'KM Restante',
        value: detail.kmRestante,
        icon: <SpeedOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />
      }
    ]
  }));

  const expandedContent = detailSections.length ? (
    <FrotaSectionsWrap>
      {detailSections.map(section => (
        <FrotaTripSection key={section.title}>
          <FrotaTripHeader>
            <div>
              <FrotaTripTitleRow>
                <LocalShippingOutlinedIcon style={{ fontSize: 20, color: '#1356d8' }} />
                <FrotaTripTitle>{section.title}</FrotaTripTitle>
              </FrotaTripTitleRow>
              {section.subtitle && section.subtitle !== emptyValue ? (
                <FrotaTripSubtitle>{section.subtitle}</FrotaTripSubtitle>
              ) : null}
            </div>

            <FrotaStatusPill $tone={getStatusTone(section.status)}>
              {section.status}
            </FrotaStatusPill>
          </FrotaTripHeader>

          <FrotaTripBlockGrid>
            {section.topMetrics.map((metric, index) => renderMetricCell({
              icon: metric.icon,
              label: metric.label,
              value: metric.value,
              withDivider: index > 0
            }))}
          </FrotaTripBlockGrid>

          <FrotaTripMetricsWrap>
            <FrotaTripRouteGrid>
              <FrotaTripCell>
                <LocationOnOutlinedIcon style={{ fontSize: 18, color: '#0eaa7b' }} />
                <FrotaTripCellText>
                  <FrotaTripCellLabel $accentColor="#0eaa7b">Origem</FrotaTripCellLabel>
                  <FrotaTripCellValue>{section.routeOrigin.location}</FrotaTripCellValue>
                  <FrotaTripCellMeta>{section.routeOrigin.address}</FrotaTripCellMeta>
                  <FrotaTripCellMeta>{section.routeOrigin.client}</FrotaTripCellMeta>
                </FrotaTripCellText>
              </FrotaTripCell>

              <FrotaTripCell $withDivider={true}>
                <LocationOnOutlinedIcon style={{ fontSize: 18, color: '#1456d9' }} />
                <FrotaTripCellText>
                  <FrotaTripCellLabel $accentColor="#1456d9">Destino</FrotaTripCellLabel>
                  <FrotaTripCellValue>{section.routeDestination.location}</FrotaTripCellValue>
                  <FrotaTripCellMeta>{section.routeDestination.address}</FrotaTripCellMeta>
                  <FrotaTripCellMeta>{section.routeDestination.client}</FrotaTripCellMeta>
                </FrotaTripCellText>
              </FrotaTripCell>
            </FrotaTripRouteGrid>

            <FrotaTripBlockGrid>
              {section.scheduleMetrics.map((metric, index) => renderMetricCell({
                icon: metric.icon,
                label: metric.label,
                value: metric.value,
                withDivider: index > 0
              }))}
            </FrotaTripBlockGrid>

            <FrotaTripBlockGrid $columns={section.distanceMetrics.length}>
              {section.distanceMetrics.map((metric, index) => renderMetricCell({
                icon: metric.icon,
                label: metric.label,
                value: metric.value,
                withDivider: index > 0
              }))}
            </FrotaTripBlockGrid>
          </FrotaTripMetricsWrap>
        </FrotaTripSection>
      ))}
    </FrotaSectionsWrap>
  ) : null;

  return (
    <ClickableCardButton
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-disabled={!onSelect}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      $selected={selected}
      $disabled={!onSelect}
    >
      <AvailabilityCardItem
        title={item.frota}
        subtitle={item.veiculo}
        selected={selected}
        headerIcon={<LocalShippingOutlinedIcon style={{ fontSize: 22, color: '#ffffff' }} />}
        headerBadges={[
          {
            caption: 'Status',
            icon: <CheckCircleOutlineIcon style={{ fontSize: 11 }} />,
            label: getDisplayValue(item.disponivelLabel),
            tone: getStatusTone(item.disponivelLabel)
          },
          {
            caption: 'Status Patio',
            icon: getStatusPatioIcon(item.statusPatio),
            label: getDisplayValue(item.statusPatioLabel),
            tone: getStatusTone(item.statusPatioLabel)
          }
        ]}
        headerControls={showPositionButton ? (
          <FrotaHeaderIconButton
            type="button"
            title={positionTooltip}
            aria-label="Ver posicao da frota"
            $status={positionStatus}
            onClick={handlePositionClick}
          >
            <LocationOnOutlinedIcon style={{ fontSize: 16 }} />
          </FrotaHeaderIconButton>
        ) : null}
        summaryItems={summaryItems}
        summaryNotice={{
          icon: <InfoOutlinedIcon style={{ fontSize: 18, color: '#5f77a6' }} />,
          label: 'Mensagem Disponibilidade',
          value: item.mensagemDisponibilidade
        }}
        expandedContent={expandedContent}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
    </ClickableCardButton>
  );
};

export default FrotaAvailabilityCard;
